import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TriangleAlert } from 'lucide-react';
import { authApi, ApiError } from '@/lib/api';
import { useAuthStore } from '@/store/auth.store';
import { homeForRole } from '@/lib/roles';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';
import { Logo } from '@/components/ui/Logo';
import { styles } from '@/styles';
import { SessionExpiredNotice } from '@/components/ui/SessionExpiredNotice';

const schema = z.object({
  email: z.string().email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});
type FormData = z.infer<typeof schema>;

/*
  The sign-in screen.

  It used to be a white card floating on a three-stop indigo gradient with a
  `shadow-2xl` and `rounded-2xl` — the look every starter template ships with.
  It is now the command navy, flat, with one square panel: the same surface
  language as the product behind it, so signing in and using the thing feel
  like the same piece of software.

  The navy is written as a literal here rather than as `bg-navbar`, because this
  page renders outside the signed-in shell and must not flip with the theme —
  a sign-in screen that is white in light mode and near-black in dark mode is
  two different products to whoever supports it over the phone.
*/
export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { isAuthenticated, user, setAuth } = useAuthStore();
  const [serverError, setServerError] = useState('');

  /* `?as=gate` only changes the wording — the destination still comes from the
     role the API returns, so a gate officer and an admin can both use this form
     and each lands in the right place. */
  const forGate = params.get('as') === 'gate';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  if (isAuthenticated && user?.role) return <Navigate to={homeForRole(user.role)} replace />;

  const onSubmit = async (data: FormData) => {
    setServerError('');
    try {
      const result = await authApi.adminLogin(data.email, data.password);
      setAuth(result.accessToken, result.user);
      navigate(homeForRole(result.user.role), { replace: true });
    } catch (err) {
      setServerError(
        err instanceof ApiError ? err.message : 'Something went wrong. Please try again.',
      );
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[hsl(218_52%_13%)] p-4">
      <div className="w-full max-w-[360px]">
        <div className="mb-5 flex items-center gap-2.5">
          <Logo size={30} tone="reversed" className="flex-shrink-0" />
          <div>
            <p className="text-[15px] font-bold leading-tight text-white">SafePass</p>
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-white/45">
              {forGate ? 'Gate officer' : 'School dashboard'}
            </p>
          </div>
        </div>

        <SessionExpiredNotice />

        <div className="rounded-lg border border-white/10 bg-card p-5">
          <h1 className="text-[15px] font-bold tracking-tight text-foreground">Sign in</h1>
          <p className={`${styles.meta} mt-0.5`}>
            {forGate
              ? 'Use the staff account the school issued you.'
              : 'Use the account your institution issued you.'}
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-4 flex flex-col gap-3" noValidate>
            <Field label="Email address" htmlFor="email" error={errors.email?.message} required>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                autoFocus
                placeholder="admin@school.edu.gh"
                invalid={!!errors.email}
                {...register('email')}
              />
            </Field>

            <Field label="Password" htmlFor="password" error={errors.password?.message} required>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                invalid={!!errors.password}
                {...register('password')}
              />
            </Field>

            {serverError && (
              /* role="alert" so a screen reader is told the attempt failed —
                 the visual reader gets a red panel, and this is its equal. */
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-destructive/25 bg-destructive/[0.06] px-2.5 py-2 text-[11px] font-medium text-[hsl(var(--record-ink-danger))]"
              >
                <TriangleAlert size={13} className="mt-[1px] flex-shrink-0" />
                {serverError}
              </div>
            )}

            <Button type="submit" variant="primary" size="lg" loading={isSubmitting} className="mt-1 w-full">
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>

        <p className="mt-4 text-center text-[11px] text-white/45">
          Are you a parent or guardian?{' '}
          <Link
            to="/parent/login"
            className="font-semibold text-white/80 underline decoration-white/30 underline-offset-2 transition-colors hover:text-white"
          >
            Sign in with your phone number
          </Link>
        </p>

        <p className="mt-3 text-center text-[10px] font-semibold uppercase tracking-[0.1em] text-white/25">
          Every child. Safe. Every day.
        </p>
      </div>
    </div>
  );
}
